import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, Stack } from '@mui/material';
import PaperFormDetails from './components/paper-form-detail';
import PaperFormCharacteristic from './components/paper-form-characteristic';
import PaperFormQuality from './components/paper-form-quality';
import PaperFormOperations from './components/paper-form-operation';
import PaperFormSecurityCompliance from './components/paper-form-security';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { Formik } from 'formik';
import { DatasetScheme } from 'validators/DatasetScheme';
import { useNavigate, useParams } from 'react-router-dom';
import { DatasetsService } from 'services/DatasetsService';
import { Toast, Utils } from 'shared/helpers';
import PageHeader from 'shared/components/PageHeader';
import ScrollToError from 'shared/components/ScrollToError';

const FormDataSets = ({ purpose = 'new' }): ReactElement => {
  const { recordId } = useParams();
  const navigation = useNavigate();

  const [recordData, setRecordData] = useState<any>(null);
  const [loading, setLoading] = useState(purpose !== 'new' ? true : false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormDatasets = useCallback(
    (values: any) => {
      setIsSubmitting(true);

      const sensitivityDataTypes = values.sensitivitytype.map((value: string) =>
        value.replace(' (Global)', '')
      );
      const payload = {
        datasetName: values.datasetName,
        owningTeam: values.owningTeam,
        contactPerson: values.contactperson,
        purposeOfUse: values.purposeofuse,
        lifecycleStage: values.lifecyclestage,
        sourceType: values.sourcetype,
        dataSensitivityClassification: values.sensitivityclass,
        approvalStatus: values.approval,
        riskTier: values.risktier,
        watermarkingTechnique: values.w3ccredentials,
        sourceDetails: values.sourcedetails,
        dataCollectionMethod: values.sensitivitycollection,
        geographicOrigin: values.geographicorigin,
        volumeSize: values.volumesize,
        retentionPolicy: values.retentionpolicy,
        accessControls: values.accessControl,
        encryption: values.encryption,
        sharingRestrictions: values.sharingrestriction,
        dataDictionaryLink: values.dictionarylink,
        metadataDescription:
          'Contains column types, question IDs, and responses',
        knownDataQualityIssues: values.dataqualityissue,
        validationSummary: values.validationsummary,
        metadataAvailable: values.metadata?.[1] ?? false,
        datasheetAvailable: values.datasheet?.[1] ?? false,
        dataModalities: values.datamodalities,
        sensitiveDataTypesPresent: sensitivityDataTypes,
        complianceTags: values.compliancetags,
        downstreamDependencies: values.downstream,
        reviewFrequency: values.frequency,
        monitoringInPlace: values.inplace,
      };

      const request =
        purpose === 'edit'
          ? DatasetsService.updateDatasets(recordId, payload)
          : DatasetsService.createDatasets(payload);

      request
        .then(() => {
          Toast.showSuccessMessage(
            `Successfully ${
              purpose === 'edit' ? 'updated' : 'created'
            } datasets`
          );
          navigation('/datasets');
        })
        .catch((e) => {
          Toast.showErrorMessage(e);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [navigation]
  );

  useEffect(() => {
    if (purpose !== 'new') {
      DatasetsService.getSingleDataset(recordId ?? null)
        .then((response) => {
          const { data: resp } = response;
          if (resp && resp.data) {
            setRecordData(resp.data);
            setLoading(false);
          }
        })
        .catch((err) => {
          Utils.handleErrorResponse(err);
        });
    }
  }, [recordId]);

  return (
    <>
      {!loading && (
        <>
          <PageHeader
            title={purpose !== 'new' ? 'Edit Dataset' : 'New Dataset'}
          />
          <Box className="ai-inventory form-datasets">
            <HeadTag
              title={purpose !== 'new' ? 'Edit Dataset' : 'New Dataset'}
            />
            <Formik
              initialValues={
                purpose !== 'new' && recordData
                  ? {
                      datamodalities: recordData?.dataModalities,
                      datamodalitiescustom: '',
                      volumesize: recordData?.volumeSize,
                      geographicorigin: recordData?.geographicOrigin,
                      sensitivityclass:
                        recordData?.dataSensitivityClassification,
                      sensitivitytype: recordData?.sensitiveDataTypesPresent,
                      sensitivitycollection: recordData?.dataCollectionMethod,
                      sourcedetails: recordData?.sourceDetails,
                      datasetName: recordData?.datasetName,
                      owningTeam: recordData?.owningTeam,
                      contactperson: recordData?.contactPerson,
                      lifecyclestage: recordData?.lifecycleStage,
                      sourcetype: recordData?.sourceType,
                      risktier: recordData?.riskTier,
                      w3ccredentials: recordData?.watermarkingTechnique,
                      purposeofuse: recordData?.purposeOfUse,
                      downstream: recordData?.downstreamDependencies,
                      frequency: recordData?.reviewFrequency,
                      inplace: recordData?.monitoringInPlace,
                      approval: recordData?.approvalStatus,
                      dataqualityissue: recordData?.knownDataQualityIssues,
                      validationsummary: recordData?.validationSummary,
                      metadata:
                        recordData?.metadataAvailable === false
                          ? [true, false]
                          : [false, true],
                      datasheet:
                        recordData?.datasheetAvailable === false
                          ? [true, false]
                          : [false, true],
                      dictionarylink: recordData?.dataDictionaryLink,
                      accessControl: recordData?.accessControls,
                      encryption: recordData?.encryption,
                      retentionpolicy: recordData?.retentionPolicy,
                      sharingrestriction: recordData?.sharingRestrictions,
                      compliancetags: recordData?.complianceTags,
                      compliancetagscustom: '',
                    }
                  : {
                      datamodalities: [],
                      datamodalitiescustom: '',
                      volumesize: '',
                      geographicorigin: '',
                      sensitivityclass: '',
                      sensitivitytype: [],
                      sensitivitycollection: '',
                      sourcedetails: '',
                      datasetName: '',
                      owningTeam: '',
                      contactperson: '',
                      lifecyclestage: '',
                      sourcetype: '',
                      risktier: '',
                      w3ccredentials: '',
                      purposeofuse: '',
                      downstream: '',
                      frequency: '',
                      inplace: '',
                      approval: 'Pending',
                      dataqualityissue: '',
                      validationsummary: '',
                      metadata: [false, true],
                      datasheet: [false, true],
                      dictionarylink: '',
                      accessControl: '',
                      encryption: '',
                      retentionpolicy: '',
                      sharingrestriction: '',
                      compliancetags: [],
                      compliancetagscustom: '',
                    }
              }
              validateOnMount
              enableReinitialize
              validationSchema={DatasetScheme}
              onSubmit={handleFormDatasets}
            >
              {({ handleSubmit }) => (
                <Box
                  className="form-dataset-wrapper"
                  component="form"
                  autoComplete="off"
                  onSubmit={handleSubmit}
                >
                  <ScrollToError />
                  <PaperFormDetails purpose={undefined} />
                  <PaperFormCharacteristic purpose={undefined} />
                  <PaperFormQuality purpose={undefined} />
                  <PaperFormOperations purpose={undefined} />
                  <PaperFormSecurityCompliance purpose={undefined} />
                  <Stack direction="row-reverse">
                    <AppPrimaryButton
                      title={
                        purpose !== 'new' ? 'Update Dataset' : 'Create Dataset'
                      }
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    />
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      onClick={() => {
                        navigation(-1);
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Formik>
          </Box>
        </>
      )}
    </>
  );
};

export default FormDataSets;
